import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import ReferMarkdownRenderer from "./ReferMarkdownRenderer";

import api from "src/store/features/api";
import {
  Box,
  Button,
  Container,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Preloader from "src/utils/Preloader";
import { LegalStyles } from "./legal.styles";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface LegalProps {
  optIn: boolean;
  setOptIn: React.Dispatch<React.SetStateAction<boolean>>;
  setFetchUrlError: React.Dispatch<React.SetStateAction<boolean>>;
  refetchUserQuery: () => void;
  closeButton?: boolean;
  onClose?: () => void;
}

export default function ReferLegalDialog({
  optIn,
  setOptIn,
  setFetchUrlError,
  refetchUserQuery,
  closeButton,
  onClose,
}: LegalProps) {
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [ready, setReady] = useState(false);
  const [warningModal, setWarningModal] = useState(false);
  const [createUrl, setCreateUrl] = useState({ skip: true });
  const classes = LegalStyles();
  const warningMessage =
    "By rejecting the Refer A Friend Terms and Conditions, we are unable to offer you access to the Refer A Friend program. Do you wish to accept the Refer A Friend Terms and Conditions?";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const boxInnerRef = useRef();
  const [showButtons, setShowButtons] = useState(false);
  const { isError, isSuccess } = api.useCreateReferralUrlQuery(undefined, createUrl);

  useEffect(() => {
    if (!acceptTerms) {
      setOptIn(false);
      // user has already accepted terms and conditions
    }
    if (acceptTerms === true) setOptIn(false);

    if (isError) {
      setFetchUrlError(true);
    }
    if (isSuccess) {
      refetchUserQuery();
      setFetchUrlError(false);
    }
  }, [acceptTerms, isError, isSuccess]);

  const createUserUrl = () => {
    setAcceptTerms(true);
    setCreateUrl({ skip: false });
    setWarningModal(false);
    setOptIn(false);
  };

  const handleClick = (userSelection: string) => {
    switch (userSelection) {
      case "Accept":
        createUserUrl();
        break;
      case "Legal Declined":
        setWarningModal(true);
        break;
      case "Warning Declined":
        setWarningModal(false);
        setOptIn(false);
        break;
    }
  };

  const WarningDialog = () => {
    return (
      <>
        {warningModal && (
          <Dialog
            sx={{ backgroundColor: "transparent" }}
            open={warningModal}
            TransitionComponent={Transition}
            style={{ zIndex: 21474830032 }}>
            <Container className={classes.container}>
              <Typography className={classes.modalMessage}>{warningMessage}</Typography>
              <ButtonGroup actionType={"Warning Declined"} />
            </Container>
          </Dialog>
        )}
      </>
    );
  };

  // reveal buttons when user has read the T&C
  const onScroll = () => {
    if (boxInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = boxInnerRef.current;
      if (scrollTop + clientHeight + 500 > scrollHeight) {
        setShowButtons(true);
      }
    }
  };

  const ButtonGroup = (props: { actionType: string }) => {
    const { actionType } = props;
    return (
      <Tooltip
        title="Please finish reading the Terms and Conditions"
        arrow
        disableHoverListener={showButtons}>
        <Grid
          sx={{ height: "100%", marginBottom: "0", marginTop: "0" }}
          container
          spacing={2}
          mb={4}>
          <Grid
            item
            xs={6}
            sx={{
              display: "flex",
              alignItems: "center",
              paddingTop: "0 !important",
            }}
            pt={0}>
            <Button
              disableRipple
              fullWidth
              variant={"outlined"}
              onClick={() => handleClick(actionType)}
              disabled={!showButtons}>
              <Typography style={{ fontWeight: 600 }}>Decline</Typography>
            </Button>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              display: "flex",
              alignItems: "center",
              paddingTop: "0 !important",
            }}
            pt={0}>
            <Button
              disableRipple
              fullWidth
              variant={"contained"}
              onClick={() => handleClick("Accept")}
              disabled={!showButtons}>
              <Typography style={{ fontWeight: 600 }}>Accept</Typography>
            </Button>
          </Grid>
        </Grid>
      </Tooltip>
    );
  };

  return (
    <>
      {/* Legal Modal */}
      <Dialog
        sx={{
          backgroundColor: "transparent",
          zIndex: "21474830031 !important",
          overflow: "auto",
        }}
        open={optIn}
        TransitionComponent={Transition}
        maxWidth={false}
        fullScreen={isMobile}
        onClose={() => onClose && onClose()}>
        <Grid item container className={classes.legalModalGrid}>
          <Typography component="h1" variant="h1" className={classes.modalTitle}>
            Caleb and Brown Updated Refer A Friend Terms and Conditions
          </Typography>
          <Box
            className={classes.scollBar}
            ref={boxInnerRef}
            onScroll={!showButtons ? onScroll : () => null}>
            <ReferMarkdownRenderer setReady={setReady} />
          </Box>
          {!closeButton ? (
            <Box className={classes.buttonWrapper}>
              {ready && <ButtonGroup actionType={"Legal Declined"} />}
              {!ready && <Preloader />}
            </Box>
          ) : (
            <Button
              disableRipple
              fullWidth
              variant={"contained"}
              onClick={() => onClose && onClose()}>
              <Typography style={{ fontWeight: 600 }}>Close</Typography>
            </Button>
          )}
        </Grid>
      </Dialog>
      {/* Warning Modal */}
      <WarningDialog />
    </>
  );
}
